import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createMarketplaceCrossLaunch } from "../api/auth";
import {
  completeOnboarding,
  getBusinessHours,
  getBusinessProfile,
  getBusinessStatus,
  getTaxSettings,
  saveBusinessHours,
  saveTaxSettings,
  updateBusinessProfile,
} from "../api/onboarding";
import { getSalonById, patchMySalonProfile } from "../api/salons";
import { getMySalonTaxes, putMySalonTaxes } from "../api/salonTaxes";
import {
  createService,
  listServices,
  updateService,
  deleteService,
} from "../api/services";
import { deleteStaff, listStaff } from "../api/staffs";
import { createStaffUser } from "../api/salonUsers";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import TimeSelect from "../components/TimeSelect";
import { StaffEditPanel } from "./StaffEdit";
import StaffCredentialModal from "../components/staff/StaffCredentialModal";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import { notifyOnboardingStatusChange } from "../utils/onboarding";
import { buildHostUrl } from "../config/hostRouting";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../utils/phone";
import { publishRentalsFeatureSnapshot } from "../context/RentalsFeatureContext";
import ClientImportWizard from "../components/clients/ClientImportWizard";
import StaffImportWizard from "../components/staff/StaffImportWizard";
import ServiceImportWizard from "../components/services/ServiceImportWizard";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_OUTLINE,
  BTN_PRIMARY,
  BTN_BASE,
  TAB_BASE,
  TAB_ACTIVE,
  TAB_INACTIVE,
} from "../ui/modalStyles";

const BUSINESS_SETUP_STEPS = [
  { id: 1, key: "profile", label: "1. Salon / Independent Professionals Profile" },
  { id: 2, key: "hours", label: "2. Salon / Independent Professionals Hours" },
  { id: 3, key: "tax", label: "3. Tax Settings" },
  { id: 4, key: "services", label: "4. Services" },
  { id: 5, key: "staff", label: "5. Staff" },
  { id: 6, key: "clients", label: "6. Clients" },
  { id: 7, key: "summary", label: "7. Summary" },
];
const INDEPENDENT_PROFESSIONAL_STEP_LABELS = {
  1: "1. Independent Professional Profile",
  2: "2. Business Hours",
  3: "3. Tax Settings",
  4: "4. Services",
  6: "5. Clients",
  7: "6. Summary",
};
const SETUP_STEP_STORAGE_KEY = "setup_wizard_step";

const clampSetupStep = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(BUSINESS_SETUP_STEPS.length, Math.trunc(parsed)));
};

const parseStepFromSearch = (search) => {
  const params = new URLSearchParams(search || "");
  const raw = params.get("step");
  if (!raw) return null;
  return clampSetupStep(raw);
};

const STAFF_ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "technician", label: "Technician" },
  { value: "apprentice", label: "Apprentice" },
];

const normalizeSalonTaxRows = (rows = []) =>
  (rows || []).map((row) => ({
    name: row.name || "",
    rate_percent:
      row.rate_percent !== undefined && row.rate_percent !== null
        ? String(row.rate_percent)
        : "",
  }));

const defaultHours = Array.from({ length: 7 }).map((_, idx) => ({
  day_of_week: idx,
  open_time: "09:00",
  close_time: "18:00",
  is_closed: false,
}));

function coerceBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return false;
}

function normalizeTime(value) {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  const parts = str.split(":");
  const hours = Number(parts[0]);
  if (Number.isNaN(hours)) return null;
  let minutesPart = parts[1] || "00";
  const digits = minutesPart.match(/^(\d+)/);
  let minutes = digits ? Number(digits[1]) : 0;
  if (Number.isNaN(minutes)) {
    minutes = 0;
  }
  minutes = Math.max(0, Math.min(59, minutes));
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function normalizeHoursRows(data) {
  if (!Array.isArray(data)) return [];
  const normalized = [];
  for (const row of data) {
    if (!row) continue;
    const dayCandidate = row.day_of_week ?? row.dayOfWeek ?? row.day;
    const dayNumber = Number(dayCandidate);
    if (
      Number.isNaN(dayNumber) ||
      dayNumber < 0 ||
      dayNumber > 6 ||
      !Number.isFinite(dayNumber)
    ) {
      continue;
    }
    const open = normalizeTime(
      row.open_time ?? row.openTime ?? row.start_time ?? row.startTime
    );
    const close = normalizeTime(
      row.close_time ?? row.closeTime ?? row.end_time ?? row.endTime
    );
    const rawClosed = row.is_closed ?? row.isClosed ?? row.closed;
    const isClosed =
      rawClosed === undefined || rawClosed === null
        ? false
        : coerceBool(rawClosed);
    normalized.push({
      day_of_week: dayNumber,
      open_time: open,
      close_time: close,
      is_closed: isClosed,
    });
  }
  return normalized.sort((a, b) => a.day_of_week - b.day_of_week);
}

export default function SetupWizard() {
  const navigate = useNavigate();
  const { token, user, updateCurrentSalon } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const setupMode = new URLSearchParams(location.search || "").get("mode");
  const stepFromUrl = parseStepFromSearch(location.search);
  const stepFromStorage =
    typeof window !== "undefined"
      ? clampSetupStep(window.localStorage.getItem(SETUP_STEP_STORAGE_KEY))
      : 1;
  const initialStep = stepFromUrl ?? stepFromStorage ?? 1;
  const [gateMsg, setGateMsg] = useState("");

  const detectTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (e) {
      console.warn("Could not detect timezone", e);
      return "";
    }
  };

  const [activeStepId, setActiveStepId] = useState(initialStep);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profilePhoneError, setProfilePhoneError] = useState("");
  const [status, setStatus] = useState(null);
  const [profile, setProfile] = useState(() => ({
    email: "",
    phone: "",
    timezone: detectTimezone(),
    address_line1: "",
    address_line2: "",
    city: "",
    province_state: "",
    postal_code: "",
    country: "",
    enable_booth_rental: false,
    enable_accounting_services: false,
  }));
  const [salonProfile, setSalonProfile] = useState(() => ({
    name: "",
  }));
  const [ticketTaxExemptEnabled, setTicketTaxExemptEnabled] = useState(false);
  const [hours, setHours] = useState(defaultHours);
  const [tax, setTax] = useState({});
  const [salonTaxes, setSalonTaxes] = useState([]);
  const initialSalonId =
    typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null;
  const candidateSalonId = user?.salon_id ?? initialSalonId ?? null;
  const salonId = candidateSalonId;
  const isIndependentProfessionalSetup =
    (user?.role || "").toLowerCase() === "independent_professional" ||
    setupMode === "independent_professional";
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [newService, setNewService] = useState({
    name: "",
    duration_minutes: 60,
    base_price: 0,
    description: "",
  });
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [newStaff, setNewStaff] = useState({
    display_name: "",
    role: "technician",
    email: "",
    phone: "",
    username: "",
    temp_password: "",
  });
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [credentialModal, setCredentialModal] = useState(null);

  const gateBanner = gateMsg ? (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 flex items-start justify-between gap-3">
      <div className="text-sm">{gateMsg}</div>
      <button
        onClick={() => setGateMsg("")}
        className="text-xs font-medium underline"
        type="button"
      >
        Dismiss
      </button>
    </div>
  ) : null;

  const setupSteps = useMemo(() => {
    if (!isIndependentProfessionalSetup) {
      return BUSINESS_SETUP_STEPS;
    }
    return BUSINESS_SETUP_STEPS.filter((step) => step.key !== "staff").map(
      (step) => ({
        ...step,
        label: INDEPENDENT_PROFESSIONAL_STEP_LABELS[step.id] || step.label,
      })
    );
  }, [isIndependentProfessionalSetup]);

  const currentStep = useMemo(
    () =>
      setupSteps.find((step) => step.id === activeStepId) ||
      setupSteps.find((step) => step.id > activeStepId) ||
      setupSteps[setupSteps.length - 1] ||
      BUSINESS_SETUP_STEPS[0],
    [activeStepId, setupSteps]
  );
  const currentStepIndex = setupSteps.findIndex((step) => step.id === currentStep.id);
  const nextStepId = setupSteps[currentStepIndex + 1]?.id || currentStep.id;
  const previousStepId = setupSteps[currentStepIndex - 1]?.id || currentStep.id;

  const goToStep = useCallback(
    (nextStep, options = {}) => {
      const requestedStep = clampSetupStep(nextStep);
      const resolvedStep =
        setupSteps.find((step) => step.id === requestedStep) ||
        setupSteps.find((step) => step.id > requestedStep) ||
        setupSteps[setupSteps.length - 1] ||
        BUSINESS_SETUP_STEPS[0];
      const normalizedStep = resolvedStep.id;
      setActiveStepId(normalizedStep);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          SETUP_STEP_STORAGE_KEY,
          String(normalizedStep)
        );
      }
      const params = new URLSearchParams(location.search || "");
      params.set("step", String(normalizedStep));
      navigate(
        {
          pathname: location.pathname || "/setup",
          search: `?${params.toString()}`,
        },
        { replace: options.replace ?? true }
      );
    },
    [location.pathname, location.search, navigate, setupSteps]
  );

  const activeServices = useMemo(
    () => (services || []).filter((s) => s.is_active !== false),
    [services]
  );
  const activeStaff = useMemo(
    () => (staff || []).filter((s) => s.is_active !== false),
    [staff]
  );

  const refreshStatus = async () => {
    if (!candidateSalonId) {
      return null;
    }
    const s = await getBusinessStatus(token, candidateSalonId);
    setStatus(s);
    publishRentalsFeatureSnapshot({
      enabled: Boolean(s?.rentals_enabled),
      settingsReady: Boolean(s?.rentals_settings_ready),
      operationalReady: Boolean(s?.rentals_operational_ready),
      rentalMode: s?.rentals_mode || "internal",
      discoverability: s?.rentals_discoverability || "private",
      marketplaceEnabled: Boolean(s?.rentals_marketplace_enabled),
      defaultSalonId: s?.rentals_default_salon_id ?? null,
      defaultPolicySetId: s?.rentals_default_policy_set_id ?? null,
      defaultBookingMode: s?.rentals_default_booking_mode || "internal",
      defaultRequiresApproval: Boolean(
        s?.rentals_default_requires_approval
      ),
    });
    const isSetupComplete = Boolean(
      s?.is_setup_complete ?? s?.onboarding_completed
    );
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "onboarding_completed",
        isSetupComplete ? "true" : "false"
      );
      notifyOnboardingStatusChange(isSetupComplete);
    }
    return s;
  };

  const loadSalonData = async (id) => {
    if (!id) {
      setServices([]);
      setStaff([]);
      return;
    }
    const [svc, stf] = await Promise.all([
      listServices({ salon_id: id }, token),
      listStaff({ salon_id: id }, token),
    ]);
    setServices(svc || []);
    setStaff(stf || []);
  };

  const refreshStaffList = async () => {
    if (!salonId) return;
    try {
      const stf = await listStaff({ salon_id: salonId }, token);
      setStaff(stf || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to refresh staff list.");
    }
  };

  const handleEditDone = useCallback(() => {
    refreshStaffList();
  }, [refreshStaffList]);

  const handleEditStaff = useCallback(
    (svc) => {
      if (!svc?.id) return;
      if (!salonId) {
        setError("Salon context is required before continuing.");
        return;
      }
      setError("");
      setEditingStaffId(svc.id);
    },
    [salonId]
  );

  const handleCancelEditStaff = useCallback(() => {
    setEditingStaffId(null);
  }, []);

  const handleAddStaff = async () => {
    if (!salonId) {
      setError("Salon context is required before continuing.");
      return;
    }
    const displayName = newStaff.display_name.trim();
    const emailValue = newStaff.email.trim();
    const tempPassword = newStaff.temp_password.trim();
    if (!displayName || !emailValue) {
      setError("Display name and email are required to create a staff user.");
      return;
    }
    setError("");
    try {
      const payload = {
        salon_id: salonId,
        display_name: displayName,
        role: newStaff.role,
        email: emailValue,
        phone: newStaff.phone?.trim() || null,
        username: newStaff.username?.trim() || undefined,
        temp_password: tempPassword || undefined,
      };
      const response = await createStaffUser(payload, token);
      setCredentialModal({
        display_name: displayName,
        salon_id: response.salon_id,
        username: response.username,
        temp_password: response.temp_password,
        must_change_password: response.must_change_password,
      });
      setNewStaff({
        display_name: "",
        role: "technician",
        email: "",
        phone: "",
        username: "",
        temp_password: "",
      });
      await refreshStaffList();
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to create staff member.";
      setError(message);
    }
  };

  const handleDeleteStaff = async (svc) => {
    if (!svc?.id) return;
    if (!salonId) {
      setError("Salon context is required before continuing.");
      return;
    }
    const ok = window.confirm(`Delete "${svc.display_name}"? This will disable the staff member.`);
    if (!ok) return;
    try {
      await deleteStaff(svc.id, token);
      await refreshStaffList();
      if (editingStaffId === svc.id) {
        handleCancelEditStaff();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to delete staff. Please try again.");
    }
  };

  const loadData = async () => {
    if (!salonId) {
      setError("Salon context is required before loading setup data.");
      return;
    }
    setLoading(true);
    try {
      const [
        profileData,
        salonDataResponse,
        hoursData,
        taxData,
        salonTaxData,
      ] = await Promise.all([
        getBusinessProfile(token),
        getSalonById(candidateSalonId, token),
        getBusinessHours(token, candidateSalonId),
        getTaxSettings(token),
        getMySalonTaxes(token),
      ]);
      const salonData = salonDataResponse?.data || {};
      const tzFromApi = profileData?.timezone || "";
      const resolvedTz = tzFromApi || detectTimezone();
      const phoneDigits = profileData?.phone
        ? toNANPDigits10(profileData.phone)
        : "";
      const businessProfileFields = { ...(profileData || {}) };
      delete businessProfileFields.name;
      setProfile((prev) => ({
        ...prev,
        ...businessProfileFields,
        phone: phoneDigits,
        timezone: resolvedTz,
      }));
      setSalonProfile((prev) => ({
        ...prev,
        name: (salonData?.name || "").trim(),
      }));
      setTicketTaxExemptEnabled(
        Boolean(salonData?.ticket_tax_exempt_enabled)
      );
      const normalizedHours = normalizeHoursRows(hoursData);
      setHours(normalizedHours.length ? normalizedHours : defaultHours);
      setTax(taxData || {});
      setSalonTaxes(normalizeSalonTaxRows(salonTaxData?.taxes || []));

      await loadSalonData(candidateSalonId);
      await refreshStatus();
    } catch (err) {
      console.error("Failed to load setup data", err);
      setError("Unable to load setup data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const msg = location.state?.onboardingGateMessage;
    if (msg) {
      setGateMsg(msg);
    }
  }, [location.state?.onboardingGateMessage, location.state?.onboardingGateNonce]);

  useEffect(() => {
    const urlStep = parseStepFromSearch(location.search);
    if (!urlStep || urlStep === activeStepId) {
      return;
    }
    setActiveStepId(urlStep);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SETUP_STEP_STORAGE_KEY, String(urlStep));
    }
  }, [location.search, activeStepId]);

  useEffect(() => {
    loadData();
  }, [salonId, token]);

  const handleProfilePhoneChange = (value) => {
    const digits = toNANPDigits10(value);
    setProfile((prev) => ({ ...prev, phone: digits }));
    if (profilePhoneError && (!digits || isValidNANP(digits))) {
      setProfilePhoneError("");
    }
  };

  const handleProfileSave = async () => {
    const phoneDigits = profile.phone ? toNANPDigits10(profile.phone) : "";
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setProfilePhoneError("Phone must be a 10-digit North American number.");
      return;
    }
    setProfilePhoneError("");
    const payload = {
      ...profile,
      phone: phoneDigits || null,
      timezone: profile.timezone || detectTimezone(),
    };
    await updateBusinessProfile(payload, token);

    const addressParts = [profile.address_line1, profile.address_line2].filter(
      Boolean
    );
    const salonPayload = {
      name: salonProfile.name?.trim() || undefined,
      address: addressParts.join(" ").trim() || undefined,
      city: profile.city || undefined,
      province: profile.province_state || undefined,
      postal_code: profile.postal_code || undefined,
    };
    try {
      await patchMySalonProfile(salonPayload, token);
    } catch (err) {
      // Keep Step 1 flow moving when salon profile patch is non-critical.
      console.warn("Unable to sync salon profile fields from setup step 1", err);
    }
    const savedSalonName = salonProfile.name?.trim();
    if (salonId && savedSalonName) {
      updateCurrentSalon({
        salon_id: salonId,
        salon_name: savedSalonName,
        name: savedSalonName,
      });
    }
  };

  const handleHoursSave = async () => {
    const savedHours = await saveBusinessHours(hours, token, candidateSalonId);
    const normalizedSavedHours = normalizeHoursRows(savedHours);
    if (normalizedSavedHours.length) {
      setHours(normalizedSavedHours);
    }
    if (typeof window !== "undefined") {
      const version = String(Date.now());
      localStorage.setItem("business_hours_version", version);
      window.dispatchEvent(
        new CustomEvent("sbin:business-hours-updated", {
          detail: { salonId: candidateSalonId, version },
        })
      );
    }
  };

  const handleTaxSave = async () => {
    const collectsSalesTax = tax.collects_sales_tax ?? true;
    const sanitizedTaxes = [];
    const seen = new Set();

    const throwValidationError = (message) => {
      const error = new Error("tax-validation");
      error.userMessage = message;
      throw error;
    };

    salonTaxes.forEach((row) => {
      const nameValue = row.name?.trim() || "";
      const hasName = Boolean(nameValue);
      const hasRate =
        row.rate_percent !== "" &&
        row.rate_percent !== null &&
        row.rate_percent !== undefined;
      if (!hasName && !hasRate) {
        return;
      }
      if (!hasName) {
        throwValidationError("Tax name is required for every tax row.");
      }
      if (!hasRate) {
        throwValidationError("Tax rate is required for every tax row.");
      }
      const normalizedName = nameValue.toLowerCase();
      if (seen.has(normalizedName)) {
        throwValidationError("Each tax name must be unique (case-insensitive).");
      }
      seen.add(normalizedName);

      const numericRate = Number(row.rate_percent);
      if (Number.isNaN(numericRate)) {
        throwValidationError("Enter a valid number for tax rates.");
      }
      if (numericRate < 0 || numericRate > 100) {
        throwValidationError("Tax rates must be between 0 and 100.");
      }
      sanitizedTaxes.push({
        name: nameValue,
        rate_percent: numericRate,
      });
    });

    await saveTaxSettings(tax, token);
    if (salonId && user?.is_super_admin) {
      await patchMySalonProfile(
        {
          ticket_tax_exempt_enabled: ticketTaxExemptEnabled,
        },
        token
      );
    }
    const payload = collectsSalesTax ? sanitizedTaxes : [];
    const updatedTaxes = await putMySalonTaxes(token, { taxes: payload });
    setSalonTaxes(normalizeSalonTaxRows(updatedTaxes?.taxes || []));
  };

  const handleAddTaxRow = () => {
    setSalonTaxes((prev) => [...prev, { name: "", rate_percent: "" }]);
  };

  const handleTaxRowChange = (idx, field, value) => {
    setSalonTaxes((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === idx ? { ...row, [field]: value } : row
      )
    );
  };

  const handleRemoveTaxRow = (idx) => {
    setSalonTaxes((prev) => prev.filter((_, rowIndex) => rowIndex !== idx));
  };

  const handleAddService = async () => {
    if (!salonId) {
      setError("Salon context is required before continuing.");
      return;
    }
    if (!newService.name) return;
    const payload = {
      ...newService,
      salon_id: salonId,
      duration_minutes: Number(newService.duration_minutes) || 30,
      base_price: Number(newService.base_price) || 0,
    };
    const created = await createService(payload, token);
    setServices((prev) => [...prev, created]);
    setNewService({ name: "", duration_minutes: 60, base_price: 0, description: "" });
    setEditingServiceId(null);
  };

  const handleEditService = (svc) => {
    setError("");
    setEditingServiceId(svc.id);
    setNewService({
      name: svc.name ?? "",
      duration_minutes: svc.duration_minutes ?? 60,
      base_price: svc.base_price ?? 0,
      description: svc.description ?? "",
    });
  };

  const handleCancelEditService = () => {
    setError("");
    setEditingServiceId(null);
    setNewService({ name: "", duration_minutes: 60, base_price: 0, description: "" });
  };

  const handleUpdateService = async () => {
    if (!editingServiceId) return;
    if (!salonId) {
      setError("Salon context is required before continuing.");
      return;
    }
    if (!newService.name) return;

    try {
      const payload = {
        name: newService.name,
        description: newService.description || null,
        duration_minutes: Number(newService.duration_minutes) || 30,
        base_price: Number(newService.base_price) || 0,
      };

      const updated = await updateService(editingServiceId, payload, token);

      setServices((prev) =>
        (prev || []).map((s) => (s.id === updated.id ? updated : s))
      );

      handleCancelEditService();
    } catch (err) {
      console.error("Update service failed", err);
      setError(err?.response?.data?.detail || "Unable to update service. Please try again.");
    }
  };

  const handleDeleteService = async (svc) => {
    if (!svc?.id) return;
    if (!salonId) {
      setError("Salon context is required before continuing.");
      return;
    }

    const ok = window.confirm(`Delete "${svc.name}"? This will disable the service.`);
    if (!ok) return;

    try {
      await deleteService(svc.id, token);

      setServices((prev) =>
        (prev || []).map((s) => (s.id === svc.id ? { ...s, is_active: false } : s))
      );

      if (editingServiceId === svc.id) {
        handleCancelEditService();
      }
    } catch (err) {
      console.error("Delete service failed", err);
      setError(err?.response?.data?.detail || "Unable to delete service. Please try again.");
    }
  };

  const handleNext = async () => {
    setError("");
    setSaving(true);
    const shouldMoveToNextStep = currentStepIndex < setupSteps.length - 1;
    try {
      if (currentStep.key === "profile") {
        await handleProfileSave();
        goToStep(nextStepId);
        try {
          await refreshStatus();
        } catch (statusErr) {
          console.warn("Unable to refresh setup status after step save", statusErr);
        }
        return;
      }
      if (currentStep.key === "hours") await handleHoursSave();
      if (currentStep.key === "tax") await handleTaxSave();
      if (currentStep.key === "services" && !salonId) {
        setError("Salon context is required before adding services.");
        setSaving(false);
        return;
      }
      if (currentStep.key === "services" && activeServices.length === 0) {
        setError("Add at least one service to continue.");
        setSaving(false);
        return;
      }
      if (
        !isIndependentProfessionalSetup &&
        currentStep.key === "staff" &&
        activeStaff.length === 0
      ) {
        setError("Add at least one staff member to continue.");
        setSaving(false);
        return;
      }
      if (shouldMoveToNextStep) {
        goToStep(nextStepId);
      }
      try {
        await refreshStatus();
      } catch (statusErr) {
        console.warn("Unable to refresh setup status after step save", statusErr);
      }
    } catch (err) {
      console.error("Save error", err);
      const message =
        err?.userMessage ||
        err?.response?.data?.detail ||
        "Unable to save this step. Please try again.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setError("");
    goToStep(previousStepId);
  };

  const handleSkipClients = () => {
    setError("");
    goToStep(nextStepId);
  };

  const myBoothShareEnabled = Boolean(
    profile.enable_booth_rental || status?.rentals_enabled
  );

  const openMyBoothShare = async () => {
    const marketplaceHost = brand?.marketplaceHostName || "myboothshare.com";
    const fallbackUrl =
      brand?.marketplaceOperatorUrl ||
      buildHostUrl(marketplaceHost, "/account/operator");
    try {
      const launch = await createMarketplaceCrossLaunch();
      const nextPath = String(launch?.next_path || "").trim() || "/account/operator";
      const url = new URL(buildHostUrl(marketplaceHost, nextPath));
      if (launch?.launch_token) {
        url.searchParams.set("launch_token", launch.launch_token);
      }
      window.location.assign(url.toString());
    } catch (launchErr) {
      console.warn("Unable to create MyBoothShare launch after setup", launchErr);
      window.location.assign(fallbackUrl);
    }
  };

  const handleFinish = async () => {
    setError("");
    setSaving(true);
    try {
      const finalStatus = await completeOnboarding(token, candidateSalonId);
      const isSetupComplete = Boolean(
        finalStatus?.is_setup_complete ?? finalStatus?.onboarding_completed
      );
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "onboarding_completed",
          isSetupComplete ? "true" : "false"
        );
        notifyOnboardingStatusChange(isSetupComplete);
      }
      if (!isSetupComplete) {
        setError("Setup is not complete yet. Please finish the required steps.");
        return;
      }
      if (!isIndependentProfessionalSetup && myBoothShareEnabled) {
        await openMyBoothShare();
        return;
      }
      navigate("/manager/schedule", { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to finish setup. Please try again.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const updateHourField = (dayIdx, field, value) => {
    setHours((prev) =>
      prev.map((row) =>
        row.day_of_week === dayIdx ? { ...row, [field]: value } : row
      )
    );
  };

  const summarySalonComplete = Boolean(salonId);
  const sectionCard =
    "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";
  const stepContainerClass =
    ["clients", "services", "staff"].includes(currentStep.key)
      ? "space-y-4"
      : sectionCard;

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  if (loading) {
    return (
      <div className="manageSectionShell min-h-screen bg-slate-100">
        <div className="manageSectionWide manage-section-wide manage-section-compact">
          {gateBanner}
          <div className="rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200">
            <div className="bg-rose-500 px-6 py-4 relative">
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                {isIndependentProfessionalSetup
                  ? "Independent Professional Setup Wizard"
                  : "Salon / Independent Professionals Setup Wizard"}
              </h1>
              <p className="text-sm md:text-base text-white/80">
                {isIndependentProfessionalSetup
                  ? "Complete these steps to use Ellyanna Booking for your independent client work."
                  : "Complete these steps to start booking as a salon owner or independent professional."}
              </p>
              <div
                className="absolute right-4 top-3 z-50"
                onClick={handleClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ModalCloseX onClick={handleClose} />
              </div>
            </div>
            <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
              <p className="text-slate-600">Loading setup data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manageSectionShell min-h-screen bg-slate-100">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
        {gateBanner}
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              {isIndependentProfessionalSetup
                ? "Independent Professional Setup Wizard"
                : "Salon / Independent Professionals Setup Wizard"}
            </h1>
            <p className="text-sm md:text-base text-white/80">
              {isIndependentProfessionalSetup
                ? "Complete your profile, hours, taxes, services, clients, and Ellyanna Booking setup."
                : "Complete these steps to start booking as a salon owner or independent professional."}
            </p>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div className="flex flex-wrap items-center gap-2">
              {setupSteps.map((step) => {
                const isActive = step.id === currentStep.id;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => goToStep(step.id)}
                    className={`${TAB_BASE} ${
                      isActive
                        ? TAB_ACTIVE
                        : `bg-white ${TAB_INACTIVE}`
                    }`}
                  >
                    {step.label}
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            <div className={stepContainerClass}>
        {currentStep.key === "profile" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <span className={LABEL_TEXT}>
                <span className="inline-flex items-center gap-2">
                  <span>
                    {isIndependentProfessionalSetup
                      ? "Independent Professional Business Name"
                      : "Salon Operating Name or Independent Professional Business Name"}
                  </span>
                  <InfoTooltip
                    label="Salon or Independent Professional Business Name"
                    text={
                      "This is the name clients see on online booking, receipts, and reports.\n" +
                      "Examples: 'Regal Nails Trainyards', 'Lyanna Nails & Spa', or 'Jenny X Independent Professional'.\n" +
                      "This can be different from your legal company name."
                    }
                  />
                </span>
              </span>
              <input
                type="text"
                value={salonProfile.name || ""}
                onChange={(e) =>
                  setSalonProfile((prev) => ({ ...prev, name: e.target.value }))
                }
                className={FIELD_CLASS}
              />
              <p className="text-xs text-slate-500">
                {isIndependentProfessionalSetup
                  ? "Tip: Use the public-facing business name clients will recognize in online booking."
                  : "Tip: Use the public-facing salon or independent professional business name. Your legal company name stays in the internal settings record."}
              </p>
            </div>
            <Input
              label="Email"
              value={profile.email || ""}
              onChange={(v) => setProfile({ ...profile, email: v })}
            />
            <div className="flex flex-col gap-1">
              <Input
                label="Phone"
                value={
                  profile.phone && isValidNANP(profile.phone)
                    ? formatNANP(profile.phone)
                    : profile.phone || ""
                }
                onChange={handleProfilePhoneChange}
              />
              {profilePhoneError && (
                <p className="text-xs text-rose-600">{profilePhoneError}</p>
              )}
            </div>
            <Input
              label="Timezone"
              value={profile.timezone || ""}
              onChange={(v) =>
                !profile.timezone && setProfile({ ...profile, timezone: v })
              }
              disabled={!!profile.timezone}
            />
            <p className="text-xs text-slate-500">
              {profile.timezone
                ? "Timezone detected automatically from your device."
                : "We could not detect your timezone automatically. Please enter it manually."}
            </p>
            <Input
              label="Address Line 1"
              value={profile.address_line1 || ""}
              onChange={(v) => setProfile({ ...profile, address_line1: v })}
            />
            <Input
              label="Address Line 2"
              value={profile.address_line2 || ""}
              onChange={(v) => setProfile({ ...profile, address_line2: v })}
            />
            <Input
              label="City"
              value={profile.city || ""}
              onChange={(v) => setProfile({ ...profile, city: v })}
            />
            <Input
              label="Province / State"
              value={profile.province_state || ""}
              onChange={(v) => setProfile({ ...profile, province_state: v })}
            />
            <Input
              label="Postal Code / ZIP Code"
              value={profile.postal_code || ""}
              onChange={(v) => setProfile({ ...profile, postal_code: v })}
            />
            <Input
              label="Country"
              value={profile.country || ""}
              onChange={(v) => setProfile({ ...profile, country: v })}
            />
            {!isIndependentProfessionalSetup && (
            <div className="col-span-1 md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <label
                htmlFor="enable_booth_rental"
                className="flex items-start gap-3 text-sm md:text-base text-slate-800"
              >
                <input
                  id="enable_booth_rental"
                  type="checkbox"
                  checked={!!profile.enable_booth_rental}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      enable_booth_rental: e.target.checked,
                    })
                  }
                  className="mt-1"
                />
                <span>
                  <span className="font-semibold">
                    Prepare this salon for MyBoothShare workspace rentals
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                    Turn this on if you want to list chairs, rooms, or stations
                    on MyBoothShare and work with independent beauty
                    professionals. After you finish this Ellyanna setup, open
                    MyBoothShare to complete your marketplace account and create
                    your first workspace listing.
                  </span>
                </span>
              </label>
            </div>
            )}
            <div className="col-span-1 md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
              <label
                htmlFor="enable_accounting_services"
                className="flex items-start gap-3 text-sm md:text-base text-slate-800"
              >
                <input
                  id="enable_accounting_services"
                  type="checkbox"
                  checked={!!profile.enable_accounting_services}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      enable_accounting_services: e.target.checked,
                    })
                  }
                  className="mt-1"
                />
                <span>
                  <span className="font-semibold">
                    I want accounting, tax, payroll, or bookkeeping support from SalonBusinessCPA
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                    Turn this on if you want help organizing records, GST/HST,
                    payroll, pay stubs, tax documents, or year-end review. You
                    can try SalonBusinessCPA free for 30 days before choosing a
                    paid accounting package.
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}

        {currentStep.key === "hours" && (
          <div className="space-y-3">
            {hours.map((row) => (
              <div
                key={row.day_of_week}
                className="flex flex-col md:flex-row md:items-center md:gap-3 border rounded px-3 py-2"
              >
                <div className="font-medium w-28">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][row.day_of_week]}
                </div>
                <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                  <input
                    type="checkbox"
                    checked={row.is_closed}
                    onChange={(e) =>
                      updateHourField(row.day_of_week, "is_closed", e.target.checked)
                    }
                  />
                  <span>Closed</span>
                </label>
                {!row.is_closed && (
                  <div className="flex gap-2 mt-2 md:mt-0 items-center">
                    <TimeSelect
                      value={row.open_time || ""}
                      onChange={(val) =>
                        updateHourField(row.day_of_week, "open_time", val)
                      }
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-600">to</span>
                    <TimeSelect
                      value={row.close_time || ""}
                      onChange={(val) =>
                        updateHourField(row.day_of_week, "close_time", val)
                      }
                      className="flex-1"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {currentStep.key === "tax" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Country"
                value={tax.country || ""}
                onChange={(v) => setTax({ ...tax, country: v })}
              />
              <Input
                label="Province / State"
                value={tax.province_state || ""}
                onChange={(v) => setTax({ ...tax, province_state: v })}
              />
              <Input
                label="Tax Registration Number"
                value={tax.tax_registration_number || ""}
                onChange={(v) => setTax({ ...tax, tax_registration_number: v })}
              />
              <Input
                label="Default Tip %"
                type="number"
                value={tax.default_tip_percentage ?? ""}
                onChange={(v) =>
                  setTax({ ...tax, default_tip_percentage: v ? Number(v) : null })
                }
              />
              <Toggle
                label="Collects sales tax"
                checked={tax.collects_sales_tax ?? true}
                onChange={(val) => setTax({ ...tax, collects_sales_tax: val })}
              />
              <Toggle
                label="Prices include tax"
                checked={tax.price_includes_tax ?? false}
                onChange={(val) => setTax({ ...tax, price_includes_tax: val })}
              />
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-sm font-semibold text-slate-800">
                Ticket tax exemption
              </div>
              {user?.is_super_admin ? (
                <>
                  <label className="mt-3 flex items-start gap-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={ticketTaxExemptEnabled}
                      onChange={(e) =>
                        setTicketTaxExemptEnabled(e.target.checked)
                      }
                      className="mt-1"
                    />
                    <span>
                      Allow this salon to use the ticket tax exempt option.
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-slate-500">
                    Only super admins can change this. If it stays off, this
                    salon cannot mark tickets as tax exempt and sales remain
                    taxable.
                  </p>
                </>
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  Only super admins can enable ticket tax exemption for this
                  salon.
                </p>
              )}
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">
                  Tax rates
                </h3>
                <button
                  type="button"
                  onClick={handleAddTaxRow}
                  disabled={!(tax.collects_sales_tax ?? true)}
                  className="text-xs text-emerald-600 underline disabled:text-slate-400"
                >
                  Add tax
                </button>
              </div>
              <p className="text-xs text-slate-500">
                {tax.collects_sales_tax
                  ? "Define each sales tax that applies to services."
                  : "Sales tax collection is disabled, so defined rates will not be applied."}
              </p>
              <div className="space-y-2">
                {salonTaxes.map((row, idx) => (
                  <div
                    key={`tax-row-${idx}`}
                    className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm"
                  >
                    <Input
                      label="Tax Name"
                      value={row.name}
                      onChange={(v) => handleTaxRowChange(idx, "name", v)}
                      disabled={!(tax.collects_sales_tax ?? true)}
                    />
                    <Input
                      label="Rate %"
                      type="number"
                      step="0.0001"
                      min="0"
                      max="100"
                      value={row.rate_percent}
                      onChange={(v) => handleTaxRowChange(idx, "rate_percent", v)}
                      disabled={!(tax.collects_sales_tax ?? true)}
                    />
                    <div className="col-span-1 md:col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveTaxRow(idx)}
                        disabled={!(tax.collects_sales_tax ?? true)}
                        className="text-xs text-rose-600 underline disabled:text-slate-400"
                      >
                        Remove row
                      </button>
                    </div>
                  </div>
                ))}
                {salonTaxes.length === 0 && tax.collects_sales_tax && (
                  <p className="text-xs text-slate-500">
                    No taxes defined yet. Use “Add tax” to create one.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {currentStep.key === "services" && (
          <div className="space-y-4">
            <ServiceImportWizard
              embedded
              initialSalonId={salonId}
              onComplete={async () => {
                await loadSalonData(candidateSalonId);
                await refreshStatus();
              }}
            />
            <div className={sectionCard}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-semibold text-slate-800">Existing Services</h3>
                  {activeServices.length === 0 && (
                    <p className="text-sm text-slate-600">No services yet.</p>
                  )}
                  <ul className="space-y-2">
                    {activeServices.map((svc) => (
                      <li
                        key={svc.id}
                        className={`border rounded px-3 py-2 text-sm flex justify-between ${
                          editingServiceId === svc.id
                            ? "border-slate-900"
                            : "border-slate-200"
                        }`}
                      >
                        <div>
                          <div className="font-medium">{svc.name}</div>
                          <div className="text-slate-500">
                            {svc.duration_minutes} mins • ${svc.base_price}
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => handleEditService(svc)}
                            className="text-xs text-slate-700 underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteService(svc)}
                            className="text-xs text-rose-600 underline"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-800">
                    {editingServiceId ? "Edit Service" : "Add a Service"}
                  </h3>
                  <Input
                    label="Name"
                    value={newService.name}
                    onChange={(v) => setNewService({ ...newService, name: v })}
                  />
                  <Input
                    label="Duration (minutes)"
                    type="number"
                    value={newService.duration_minutes}
                    onChange={(v) =>
                      setNewService({ ...newService, duration_minutes: v })
                    }
                  />
                  <Input
                    label="Price"
                    type="number"
                    value={newService.base_price}
                    onChange={(v) => setNewService({ ...newService, base_price: v })}
                  />
                  <Input
                    label="Description"
                    value={newService.description}
                    onChange={(v) => setNewService({ ...newService, description: v })}
                  />
                  {editingServiceId ? (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleUpdateService}
                        className="flex-1 rounded bg-slate-900 text-white py-2 text-sm"
                      >
                        Update Service
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEditService}
                        className="flex-1 rounded border border-slate-300 bg-white text-slate-900 py-2 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAddService}
                      className="w-full rounded bg-slate-900 text-white py-2 text-sm"
                    >
                      Add Service
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep.key === "staff" && (
          <div className="space-y-4">
            <StaffImportWizard
              embedded
              initialSalonId={salonId}
              onComplete={async () => {
                await refreshStaffList();
                await refreshStatus();
              }}
            />
            <div className={sectionCard}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-semibold text-slate-800">Team</h3>
                  {activeStaff.length === 0 && (
                    <p className="text-sm text-slate-600">No staff yet.</p>
                  )}
                  <ul className="space-y-2">
                    {activeStaff.map((member) => (
                      <li
                        key={member.id}
                        className={`border rounded px-3 py-2 text-sm flex justify-between ${
                          editingStaffId === member.id ? "border-slate-900" : "border-slate-200"
                        }`}
                      >
                        <div>
                          <div className="font-medium">{member.display_name}</div>
                          <div className="text-slate-500">
                            {member.role} {member.email ? `• ${member.email}` : ""}
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => handleEditStaff(member)}
                            className="text-xs text-slate-700 underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteStaff(member)}
                            className="text-xs text-rose-600 underline"
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  {editingStaffId ? (
                    <StaffEditPanel
                      staffId={editingStaffId}
                      embedded
                      onDone={handleEditDone}
                      onCancel={handleCancelEditStaff}
                    />
                  ) : (
                    <div className="space-y-3">
                    <h3 className="font-semibold text-slate-800">Add Staff</h3>
                      <Input
                        label="Name"
                        value={newStaff.display_name}
                        onChange={(v) => setNewStaff({ ...newStaff, display_name: v })}
                      />
                      <label className="flex flex-col gap-2">
                        <span className={LABEL_TEXT}>Role</span>
                        <select
                          value={newStaff.role}
                          onChange={(e) =>
                            setNewStaff({ ...newStaff, role: e.target.value })
                          }
                          className={FIELD_CLASS}
                        >
                          {STAFF_ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <Input
                        label="Username (optional)"
                        value={newStaff.username}
                        onChange={(v) => setNewStaff({ ...newStaff, username: v })}
                        placeholder="staff-username"
                      />
                      <Input
                        label="Temporary Password (optional)"
                        value={newStaff.temp_password}
                        onChange={(v) =>
                          setNewStaff({ ...newStaff, temp_password: v })
                        }
                        type="password"
                        placeholder="At least 8 characters"
                      />
                      <p className="text-xs text-slate-500">
                        If left blank, Ellyanna will generate one.
                      </p>
                      <Input
                        label="Email"
                        value={newStaff.email}
                        onChange={(v) => setNewStaff({ ...newStaff, email: v })}
                        required
                      />
                      <Input
                        label="Phone"
                        value={newStaff.phone}
                        onChange={(v) => setNewStaff({ ...newStaff, phone: v })}
                      />
                      <button
                        type="button"
                        onClick={handleAddStaff}
                        className="w-full rounded bg-slate-900 text-white py-2 text-sm"
                      >
                        Add Staff Member
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep.key === "clients" && (
          <div className="space-y-4">
            <ClientImportWizard embedded initialSalonId={salonId} />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSkipClients}
                className={BTN_OUTLINE}
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {currentStep.key === "summary" && (
          <div className="space-y-3">
            <p className="text-slate-600">
              Review the checklist below. All required items must be complete to finish.
            </p>
            {!isIndependentProfessionalSetup && myBoothShareEnabled ? (
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm leading-6 text-sky-950">
                <p className="font-semibold">Next step: open MyBoothShare</p>
                <p className="mt-1">
                  This setup prepares your salon for workspace rentals, but your
                  MyBoothShare account and listings are completed inside
                  MyBoothShare. After finishing, you will be taken there to set
                  up your marketplace profile, add available chairs, rooms, or
                  stations, and publish listings for independent professionals.
                </p>
              </div>
            ) : null}
            <SummaryRow
              label={isIndependentProfessionalSetup ? "Professional profile" : "Salon profile"}
              ok={status?.has_profile}
            />
            <SummaryRow
              label={isIndependentProfessionalSetup ? "Business hours" : "Salon hours"}
              ok={status?.has_hours}
            />
            <SummaryRow label="Tax settings" ok={status?.has_tax_settings} />
            <SummaryRow
              label={isIndependentProfessionalSetup ? "Ellyanna booking account" : "Salon"}
              ok={summarySalonComplete}
            />
            <SummaryRow label="Services" ok={status?.has_services} />
            {!isIndependentProfessionalSetup && (
              <SummaryRow label="Staff" ok={status?.has_staff} />
            )}
          </div>
        )}
      </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStepIndex <= 0}
                className={BTN_OUTLINE}
              >
                Back
              </button>
              {currentStep.key === "summary" ? (
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={saving}
                  className={`${BTN_BASE} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60`}
                >
                  {saving
                    ? "Finishing..."
                    : !isIndependentProfessionalSetup && myBoothShareEnabled
                      ? "Finish and open MyBoothShare"
                      : "Finish and go to calendar"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving}
                  className={BTN_PRIMARY}
                >
                  {saving ? "Saving..." : "Save & Continue"}
                </button>
              )}
            </div>
          </div>
        </div>
        <StaffCredentialModal
          credential={credentialModal}
          onClose={() => setCredentialModal(null)}
        />
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  readOnly = false,
  required = false,
  placeholder = "",
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className={LABEL_TEXT}>{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        required={required}
        className={FIELD_CLASS}
      />
    </label>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function SummaryRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between border rounded px-3 py-2 text-sm">
      <span>{label}</span>
      <span className={ok ? "text-emerald-600" : "text-rose-600"}>
        {ok ? "Complete" : "Missing"}
      </span>
    </div>
  );
}
